/**
 * CreateAccountModal — warm-ivory create flow for CRM accounts.
 *
 * Wires to existing backend POST /api/v1/accounts. Unlike CreateCustomerModal,
 * this modal exposes the account `type` field (customer / vendor / partner /
 * prospect) so the same flow serves both CustomersPage and AccountsPage.
 *
 * Apr 24 2026 — extracted from AccountsPage inline modal, retokenised.
 * UI-only · no backend changes · tenant-agnostic (D-09).
 */

import { useState } from 'react';
import {
  X, Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { api } from '@/utils/api';

const TYPES: Array<{ value: 'customer' | 'vendor' | 'partner' | 'prospect'; label: string }> = [
  { value: 'customer', label: 'Customer' },
  { value: 'vendor',   label: 'Vendor' },
  { value: 'partner',  label: 'Partner' },
  { value: 'prospect', label: 'Prospect' },
];

const INDUSTRIES = [
  'Manufacturing', 'Construction', 'Real Estate', 'Agriculture', 'Retail',
  'Hospitality', 'Education', 'Healthcare', 'Technology', 'Trading', 'Other',
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (account: any) => void;
  defaultType?: 'customer' | 'vendor' | 'partner' | 'prospect';
}

interface GstEnrichment {
  tradeName?: string | null;
  legalName?: string | null;
  gstStatus?: string | null;
  gstRegistrationDate?: string | null;
  gstBusinessType?: string | null;
  gstLastUpdated?: string | null;
  gstRawData?: any;
}

interface FieldErrors {
  [key: string]: string | undefined;
}

export function CreateAccountModal({ open, onClose, onCreated, defaultType = 'customer' }: Props) {
  const [saving, setSaving] = useState(false);
  const [hasGst, setHasGst] = useState<boolean | null>(null);
  const [gstFetching, setGstFetching] = useState(false);
  const [gstFetched, setGstFetched] = useState(false);
  const [gstError, setGstError] = useState('');
  const [gstEnrichment, setGstEnrichment] = useState<GstEnrichment | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [topError, setTopError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '', type: defaultType, industry: '', phone: '', email: '',
    gstin: '', pan: '', stateCode: '', billingAddress: '', notes: '',
  });

  const [contactPerson, setContactPerson] = useState({
    firstName: '', lastName: '', phone: '', email: '',
  });

  const reset = () => {
    setHasGst(null);
    setGstFetched(false);
    setGstError('');
    setGstEnrichment(null);
    setFieldErrors({});
    setTopError(null);
    setForm({
      name: '', type: defaultType, industry: '', phone: '', email: '',
      gstin: '', pan: '', stateCode: '', billingAddress: '', notes: '',
    });
    setContactPerson({ firstName: '', lastName: '', phone: '', email: '' });
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setGstFetching(true);
    setGstError('');
    setGstFetched(false);
    setGstEnrichment(null);
    try {
      const res = await api.get(`/accounts/gst-lookup/${gstin}`);
      const d = res.data.data;
      setForm(prev => ({
        ...prev,
        name: d.companyName || d.tradeName || d.legalName || prev.name,
        pan: d.pan || prev.pan,
        stateCode: d.stateCode || prev.stateCode,
        billingAddress: d.address || prev.billingAddress,
      }));

      if (d.address && d.source === 'api') {
        try {
          const addrRes = await api.post('/ai/parse-address', {
            rawAddress: d.address, stateName: d.stateName || '',
          });
          const ap = addrRes.data.data;
          if (ap?.parsed) {
            const parts = [ap.line1, ap.city, ap.state, ap.pincode].filter(Boolean);
            if (parts.length > 0) {
              setForm(prev => ({ ...prev, billingAddress: parts.join(', ') }));
            }
          }
        } catch { /* AI parse failed — raw address stays */ }
      }

      if (d.source === 'api') {
        setGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
        });
      }
      if (d.apiError) setGstError(`GST API: ${d.apiError} — using offline data`);
      setGstFetched(true);
    } catch (err: any) {
      setGstError(err.response?.data?.error || 'Failed to fetch GST details');
    } finally {
      setGstFetching(false);
    }
  };

  const handleSave = async () => {
    setFieldErrors({});
    setTopError(null);

    if (!form.name.trim()) {
      setFieldErrors({ name: 'Account name is required' });
      return;
    }

    try {
      setSaving(true);
      const payload: any = {
        name: form.name.trim(),
        type: form.type,
        industry: form.industry || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        gstin: form.gstin || undefined,
        pan: form.pan || undefined,
        stateCode: form.stateCode || undefined,
        billingAddress: form.billingAddress || undefined,
        ...(gstEnrichment || {}),
      };
      if (form.notes) payload.customFields = { notes: form.notes };

      const accRes = await api.post('/accounts', payload);
      const newAcc = accRes.data.data;

      if (contactPerson.firstName && newAcc?.id) {
        try {
          await api.post('/contacts', {
            firstName: contactPerson.firstName,
            lastName: contactPerson.lastName || '',
            phone: contactPerson.phone || form.phone || undefined,
            email: contactPerson.email || form.email || undefined,
            accountId: newAcc.id,
            customerType: 'b2b',
            source: 'Account Creation',
          });
        } catch { /* non-critical */ }
      }

      reset();
      onCreated?.(newAcc);
      onClose();
    } catch (err: any) {
      const data = err.response?.data;
      if (Array.isArray(data?.details)) {
        const next: FieldErrors = {};
        for (const d of data.details) {
          const key = Array.isArray(d.path) ? d.path[0] : d.path;
          if (key && !next[key]) next[key] = d.message || 'Invalid value';
        }
        setFieldErrors(next);
        setTopError(data?.error || 'Please correct the highlighted fields.');
      } else {
        setTopError(data?.error || err.message || 'Failed to save account');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="aura-root fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(27, 23, 19, 0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleClose}
    >
      <div
        className="mx-4 w-full max-h-[90vh] overflow-y-auto"
        style={{
          maxWidth: 560,
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4"
          style={{
            background: 'var(--ivory-1)',
            borderBottom: '1px solid var(--line)',
            borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--ff-serif)',
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: -0.01,
                color: 'var(--ink-0)',
              }}
            >
              New Account
            </h2>
            <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
              Create a customer, vendor, partner, or prospect record
            </p>
          </div>
          <button
            onClick={handleClose}
            className="btn btn-ghost"
            style={{ padding: 6, height: 'auto' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-4" style={{ color: 'var(--ink-1)' }}>
          {topError && (
            <div
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm"
              style={{
                background: 'var(--danger-bg)',
                color: 'var(--danger)',
                border: '1px solid #EFCFCA',
              }}
            >
              <AlertCircle size={14} />
              {topError}
            </div>
          )}

          {/* Type selector (always visible — this is what distinguishes Account from Customer modal) */}
          <Field label="Account type" required>
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map(t => {
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, type: t.value })}
                    className="btn"
                    style={{
                      justifyContent: 'center',
                      height: 34,
                      borderColor: active ? 'var(--ink-0)' : 'var(--line)',
                      background: active ? 'var(--ink-0)' : 'var(--ivory-0)',
                      color: active ? 'var(--ivory-0)' : 'var(--ink-1)',
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Step 1: GST yes/no */}
          {hasGst === null && (
            <div className="flex flex-col items-center gap-4 py-6">
              <p className="text-sm" style={{ color: 'var(--ink-1)' }}>
                Does this account have a GSTIN?
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setHasGst(true)}
                  className="btn btn-outline"
                  style={{ height: 40, padding: '0 16px' }}
                >
                  <CheckCircle2 size={16} style={{ color: 'var(--forest-600)' }} />
                  Yes, has GST
                </button>
                <button
                  type="button"
                  onClick={() => setHasGst(false)}
                  className="btn btn-outline"
                  style={{ height: 40, padding: '0 16px' }}
                >
                  <X size={16} style={{ color: 'var(--ink-3)' }} />
                  No GST
                </button>
              </div>
            </div>
          )}

          {/* Step 2a: With GST */}
          {hasGst === true && (
            <>
              <Field label="GSTIN" required error={fieldErrors.gstin}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={form.gstin}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
                      setForm({ ...form, gstin: v });
                      setGstFetched(false);
                      setGstError('');
                      if (v.length === 15) handleGstLookup(v);
                    }}
                    placeholder="15-character GSTIN"
                    maxLength={15}
                    autoFocus
                    className="ivory-input mono"
                    style={{ flex: 1 }}
                  />
                  {gstFetching && <Loader2 size={18} className="animate-spin" style={{ color: 'var(--saffron-600)' }} />}
                  {gstFetched && <CheckCircle2 size={18} style={{ color: 'var(--forest-600)' }} />}
                </div>
                {gstError && (
                  <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                    <AlertCircle size={12} /> {gstError}
                  </p>
                )}
                {gstFetched && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {gstEnrichment?.gstStatus && (
                      <span className={`chip ${gstEnrichment.gstStatus === 'Active' ? 'chip-green' : 'chip-red'}`}>
                        GST: {gstEnrichment.gstStatus}
                      </span>
                    )}
                    {gstEnrichment?.gstBusinessType && (
                      <span className="chip chip-blue">{gstEnrichment.gstBusinessType}</span>
                    )}
                    {gstEnrichment?.gstRegistrationDate && (
                      <span className="chip">Reg: {gstEnrichment.gstRegistrationDate}</span>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setHasGst(null); setForm(prev => ({ ...prev, gstin: '', pan: '', stateCode: '' })); setGstFetched(false); }}
                  className="mt-2 text-xs underline"
                  style={{ color: 'var(--ink-3)' }}
                >
                  Go back
                </button>
              </Field>

              <CommonFields form={form} setForm={setForm} fieldErrors={fieldErrors} showPanState />
            </>
          )}

          {/* Step 2b: No GST */}
          {hasGst === false && (
            <>
              <CommonFields form={form} setForm={setForm} fieldErrors={fieldErrors} />
              <button
                type="button"
                onClick={() => setHasGst(null)}
                className="text-xs underline"
                style={{ color: 'var(--ink-3)' }}
              >
                Go back
              </button>
            </>
          )}

          {/* Notes */}
          {hasGst !== null && (
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="ivory-input"
                placeholder="Anything else worth remembering…"
              />
            </Field>
          )}

          {/* Contact person */}
          {hasGst !== null && (
            <div className="pt-2" style={{ borderTop: '1px solid var(--ivory-3)' }}>
              <p className="kpi-label mb-2 mt-3">Contact person (recommended)</p>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First name"
                  value={contactPerson.firstName}
                  onChange={(e) => setContactPerson(p => ({ ...p, firstName: e.target.value }))}
                  className="ivory-input"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={contactPerson.lastName}
                  onChange={(e) => setContactPerson(p => ({ ...p, lastName: e.target.value }))}
                  className="ivory-input"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={contactPerson.phone}
                  onChange={(e) => setContactPerson(p => ({ ...p, phone: e.target.value }))}
                  className="ivory-input"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={contactPerson.email}
                  onChange={(e) => setContactPerson(p => ({ ...p, email: e.target.value }))}
                  className="ivory-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasGst !== null && (
          <div
            className="flex items-center justify-end gap-3 px-6 py-4"
            style={{
              borderTop: '1px solid var(--line)',
              background: 'var(--ivory-1)',
            }}
          >
            <button onClick={handleClose} className="btn btn-outline" disabled={saving}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="btn btn-primary"
              style={{ opacity: (saving || !form.name.trim()) ? 0.6 : 1 }}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Saving…' : 'Create account'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .aura-root .ivory-input {
          width: 100%;
          padding: 8px 10px;
          font-size: var(--fs-sm);
          font-family: inherit;
          color: var(--ink-0);
          background: var(--ivory-0);
          border: 1px solid var(--line);
          border-radius: var(--r-sm);
          transition: border-color .12s, box-shadow .12s;
          outline: none;
        }
        .aura-root .ivory-input:focus {
          border-color: var(--saffron-600);
          box-shadow: 0 0 0 3px var(--saffron-50);
        }
        .aura-root textarea.ivory-input { resize: vertical; min-height: 56px; }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function Field({
  label, required, error, children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="mb-1 block text-sm"
        style={{ color: 'var(--ink-1)', fontWeight: 500 }}
      >
        {label}
        {required && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

function CommonFields({
  form, setForm, fieldErrors, showPanState,
}: {
  form: any;
  setForm: (f: any) => void;
  fieldErrors: FieldErrors;
  showPanState?: boolean;
}) {
  return (
    <>
      <Field label="Account name" required error={fieldErrors.name}>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Acme Traders Pvt Ltd"
          className="ivory-input"
          autoFocus={!showPanState}
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Industry">
          <select
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            className="ivory-input"
          >
            <option value="">Select industry</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 98xxxxxxxx"
            className="ivory-input"
          />
        </Field>
      </div>
      <Field label="Email" error={fieldErrors.email}>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="billing@company.com"
          className="ivory-input"
        />
      </Field>
      {showPanState && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="PAN">
            <input
              type="text"
              value={form.pan}
              onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
              placeholder="AAAAA0000A"
              className="ivory-input mono"
              maxLength={10}
            />
          </Field>
          <Field label="State code">
            <input
              type="text"
              value={form.stateCode}
              onChange={(e) => setForm({ ...form, stateCode: e.target.value })}
              placeholder="06"
              className="ivory-input"
              maxLength={2}
            />
          </Field>
        </div>
      )}
      <Field label="Billing address" error={fieldErrors.billingAddress}>
        <textarea
          value={form.billingAddress}
          onChange={(e) => setForm({ ...form, billingAddress: e.target.value })}
          rows={2}
          placeholder="Street, city, pincode…"
          className="ivory-input"
        />
      </Field>
    </>
  );
}
